import React, { useState, useEffect, useRef } from 'react';
import proptypes from 'prop-types';
import {
  params,
  extend,
  string as stringType,
  useLazyDataModel,
  customType,
  arrayOf
} from '@thd-nucleus/data-sources';
import {
  Button, LoadingButton, DrawerBody, DrawerFooter, Typography
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { triggerAnalytics } from '../helpers';
import {
  PROJECT_ALREADY_EXISTS_ERROR_1,
  PROJECT_ALREADY_EXISTS_ERROR_2,
  PROJECT_ALREADY_EXISTS_MESSAGE,
  PROJECT_COPY,
  PROJECT_CREATE_CLICK,
  PROJECT_CREATE_SUCCESS,
  PROJECT_CREATE_OVERLAY
} from '../constants';

import { ProjectInformationForm } from '../ProjectForms/ProjectInformationForm';
import { JobsiteInformationForm } from '../ProjectForms/JobsiteInformationForm';

const { func, bool } = proptypes;

export const CreateAProjectContent = ({
  handleClose,
  onCreate,
  isCopyProject,
  isAddToDrawer,
  open
}) => {
  const [showErrors, setShowErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProjectInfoError, setIsProjectInfoError] = useState(false);
  const [isJobsiteError, setIsJobsiteError] = useState(false);
  const [projectInformation, setProjectInformation] = useState({});
  const [deliveryPreferences, setDeliveryPreferences] = useState({});
  const [contacts, setContacts] = useState({});
  const [jobsiteInformation, setJobsiteInformation] = useState({});
  const [duplicateNameError, setDuplicateNameError] = useState('');
  const { svocID } = useThdCustomer();
  const projectForm = useRef();
  const [createProject, createProjectResponse] = useLazyDataModel('createProject', {
    variables: {},
    context: { withAuth: true },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    // Trigger analytics event when the drawer is opened - this is also used in CreateAProject.jsx when opening from the
    // CreateAProject button, but that doesn't apply when this component is used within another drawer.
    // This is the workaround to ensure the event is still triggered in that scenario. Rework needed when we have a
    // more robust analytics solution.
    if (isAddToDrawer) {
      triggerAnalytics(PROJECT_CREATE_OVERLAY, {});
    }
  }, []);

  const onClose = (isCreated = false) => {
    setProjectInformation({});
    setJobsiteInformation({});
    setDeliveryPreferences({});
    setContacts({});
    setShowErrors(false);
    if (isCreated) {
      // Handle any created-specific logic here
    }
    if (!isAddToDrawer) {
      handleClose();
    }
  };

  const handleFormSubmit = async (submitEvent) => {
    submitEvent.preventDefault();

    triggerAnalytics(PROJECT_CREATE_CLICK, {});

    const {
      estimatedStartDate,
      estimatedEndDate
    } = projectInformation;

    const hasStartWithoutEnd = deliveryPreferences.jobSiteHoursStart
      && !deliveryPreferences.jobSiteHoursEnd;

    if (isProjectInfoError || isJobsiteError || !projectInformation.projectName || hasStartWithoutEnd) {
      setShowErrors(true);
      return;
    }

    if (isCopyProject) {
      setIsLoading(true);
    }
    let jobAddress = { ...jobsiteInformation };
    let contactsAddn = { ...contacts, contactType: 'DELIVERY' };
    let isValidAddress = Object.keys(jobAddress).length !== 0;
    if (isValidAddress && jobAddress.zipCode === '') {
      delete jobAddress.zipCode;
      isValidAddress = false;
    }
    if (isValidAddress) {
      jobAddress.country = 'US';
      jobAddress.addressType = 'JOB_SITE';
    }

    // Convert strings containing "yes"/"no" to boolean for hasStairs
    let processedDeliveryPreferences = { ...deliveryPreferences };

    // Convert 12-hour AM/PM display values to 24-hour HH:MM format for the API
    const convertTo24HourFormat = (timeStr) => {
      if (!timeStr) return timeStr;
      const match = timeStr.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
      if (!match) return timeStr;
      let hour = parseInt(match[1], 10);
      const minutes = match[2];
      const period = match[3].toUpperCase();
      if (period === 'AM' && hour === 12) hour = 0;
      if (period === 'PM' && hour !== 12) hour += 12;
      return `${String(hour).padStart(2, '0')}:${minutes}`;
    };

    if (processedDeliveryPreferences.jobSiteHoursStart) {
      processedDeliveryPreferences.jobSiteHoursStart = convertTo24HourFormat(
        processedDeliveryPreferences.jobSiteHoursStart
      );
    }
    if (processedDeliveryPreferences.jobSiteHoursEnd) {
      processedDeliveryPreferences.jobSiteHoursEnd = convertTo24HourFormat(
        processedDeliveryPreferences.jobSiteHoursEnd
      );
    }

    if (processedDeliveryPreferences.hasStairs && typeof processedDeliveryPreferences.hasStairs === 'string') {
      const hasStairsLower = processedDeliveryPreferences.hasStairs.toLowerCase();
      if (hasStairsLower.includes('yes')) {
        processedDeliveryPreferences.hasStairs = true;
      } else if (hasStairsLower.includes('no')) {
        processedDeliveryPreferences.hasStairs = false;
      }
    }

    // Merge propertyTypeOther into propertyType so the API receives the actual value
    if (processedDeliveryPreferences.propertyType === 'Other'
      && processedDeliveryPreferences.propertyTypeOther) {
      processedDeliveryPreferences.propertyType = processedDeliveryPreferences.propertyTypeOther;
    }
    delete processedDeliveryPreferences.propertyTypeOther;

    const object = {
      variables: {
        accountId: svocID,
        ...projectInformation,
        jobSiteAddress: !isValidAddress ? undefined : { ...jobAddress },
        deliveryPreferences: processedDeliveryPreferences,
        contacts: [contactsAddn],
        estimatedStartDate:
          estimatedStartDate === undefined ? undefined : new Date(estimatedStartDate).toISOString(),
        estimatedEndDate:
          estimatedEndDate === undefined ? undefined : new Date(estimatedEndDate).toISOString()
      }
    };

    await createProject({
      variables: {
        accountId: svocID,
        ...projectInformation,
        jobSiteAddress: !isValidAddress ? undefined : { ...jobAddress },
        deliveryPreferences: processedDeliveryPreferences,
        contacts: [contactsAddn],
        estimatedStartDate:
          estimatedStartDate === undefined ? undefined : new Date(estimatedStartDate).toISOString(),
        estimatedEndDate:
          estimatedEndDate === undefined ? undefined : new Date(estimatedEndDate).toISOString()
      }
    });
  };

  const checkForAndSetAlreadyExistError = (error) => {
    const gqlErrors = error?.graphQLErrors;
    let errorMessage = '';
    if (gqlErrors?.some((err) => err?.message.includes(PROJECT_ALREADY_EXISTS_ERROR_1) || err?.message.includes(PROJECT_ALREADY_EXISTS_ERROR_2))) {
      errorMessage = PROJECT_ALREADY_EXISTS_MESSAGE;
    }
    setDuplicateNameError(errorMessage);
  };

  useEffect(() => {
    if (createProjectResponse?.data?.createProject && createProjectResponse?.data?.createProject?.id) {
      const isType = projectInformation?.projectType ? 'y' : 'n';
      // const isDesc = projectInformation?.notes ? 'y' : 'n';
      const isAddress = jobsiteInformation?.addressLine1 ? 'y' : 'n';
      const isZip = jobsiteInformation?.zipCode ? 'y' : 'n';

      // In the format of: “projects: project created: type <y/n>: desc <y/n>: address <y/n>: zip <y/n>”
      // eslint-disable-next-line max-len
      const settingsString = `projects: project created: type ${isType}: address ${isAddress}: zip ${isZip}`;
      triggerAnalytics(PROJECT_CREATE_SUCCESS,
        {
          settings: settingsString,
          projectId: createProjectResponse?.data?.createProject?.id
        }
      );

      onCreate(createProjectResponse?.data?.createProject);
      onClose(true);
    } else if (createProjectResponse?.error) {
      checkForAndSetAlreadyExistError(createProjectResponse?.error);
    }
  }, [createProjectResponse?.data, createProjectResponse?.error]);

  return (
    <>
      <DrawerBody data-component="CreateAProject">
        <form
          className="sui-grid sui-gap-6"
          autoComplete="off"
          noValidate
          onSubmit={handleFormSubmit}
          ref={projectForm}
        >
          <Typography variant="body-lg" weight="bold">
            Project Information
          </Typography>
          <ProjectInformationForm
            showErrors={showErrors}
            projectInformation={projectInformation}
            setProjectInformation={setProjectInformation}
            isProjectInfoError={isProjectInfoError}
            setIsProjectInfoError={setIsProjectInfoError}
            deliveryPreferences={deliveryPreferences}
            setDeliveryPreferences={setDeliveryPreferences}
            contacts={contacts}
            setContacts={setContacts}
            open={open}
            duplicateNameError={duplicateNameError}
            jobsiteInformation={jobsiteInformation}
            setJobsiteInformation={setJobsiteInformation}
            isJobsiteError={isJobsiteError}
            setIsJobsiteError={setIsJobsiteError}
          />
        </form>
      </DrawerBody>
      <DrawerFooter position="center">
        <div className="sui-flex sui-flex-col sui-gap-2">
          <LoadingButton
            data-testid="create-project-submit-button"
            variant="primary"
            fullWidth
            loading={isLoading}
            onClick={handleFormSubmit}
            type="submit"
          > Create Project
          </LoadingButton>
          <Button
            data-testid="create-project-cancel-button"
            variant="secondary"
            fullWidth
            onClick={handleClose}
            type="submit"
          > Cancel
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
};

CreateAProjectContent.propTypes = {
  // callback when user clicks the form button
  // return parameters: { id, projectAccessType, project }
  onCreate: func,
  isCopyProject: bool,
  isAddToDrawer: bool,
  handleClose: func,
  open: bool,
};

CreateAProjectContent.defaultProps = {
  onCreate: () => { },
  isCopyProject: false,
  isAddToDrawer: false,
  handleClose: () => {},
  open: false,
};

CreateAProjectContent.displayName = 'CreateAProjectContent';

CreateAProjectContent.dataModel = extend(
  {
    createProject: params({
      accountId: stringType().isRequired(),
      projectName: stringType().isRequired(),
      projectType: stringType(),
      estimatedStartDate: stringType(),
      estimatedEndDate: stringType(),
      // notes: stringType(),
      jobSiteAddress: customType('JobSiteAddress').shape({
        addressType: customType('addressType').enum(['ASSORTMENT_SIP', 'FULFILLMENT', 'JOB_SITE']),
        addressLine1: stringType().isRequired(),
        addressLine2: stringType(),
        zipCode: stringType().isRequired(),
        city: stringType().isRequired(),
        state: stringType().isRequired(),
        country: stringType().isRequired()
      }),
      // assortmentZip: stringType(),
      deliveryPreferences: customType('DeliveryPreferencesInput').shape({
        siteConditions: stringType(),
        deliveryInstructions: stringType(),
        preferredPackagePlacement: stringType(),
        securityCode: stringType(),
        lockboxCode: stringType(),
        lockboxLocation: stringType(),
        deliveryType: stringType(),
        propertyType: stringType(),
        hasStairs: customType('boolean'),
      }),
      contacts: arrayOf(customType('ContactUpdate').shape({
        id: stringType(),
        name: stringType(),
        phone: stringType(),
        contactType: stringType(),
      }))
    }).mutation().shape({
      id: stringType(),
      projectName: stringType()
    })
  },
  JobsiteInformationForm
);
